<template>
    <div class="checklist-template-index">
        <transition mode="out-in">
            <div class="mt-4 grid grid-cols-8 grid-flow-row gap-4">
                <div class="col-span-8 p-4 relative border-2 rounded-lg">
                    
                        <p class="inline-block text-gray-600 text-base font-bold mr-2">Roster Checklist Settings</p>
                        <el-form label-position="top" size="small">
                            <div class="col-span-12 mt-2 md:col-span-6 lg:col-span-4">
                                <div v-if="!featurePermissions['featureEnabledRosterChecklist']">
                                    <h1>Upgrade Required</h1>
                                    <p>
                                        To enable Roster checklist features, you'll need to upgrade your plan. Please contact Hera
                                        Support by chatting with us below.
                                    </p>
                                </div>
                                <div>Enable Roster Checklists for {{ userInfo.tenant.companyName }}</div>
                                <el-switch
                                    :class="{'switch-loading': permissionRosterChecklist.loading}"
                                    v-model="permissionRosterChecklist.value"
                                    active-color="#13ce66"
                                    :disabled="disabledPermissionRosterChecklist"
                                    @change="toggleRosterChecklistFeature">
                                </el-switch>
                            </div>
                        </el-form>
                    
                </div>
            </div>
        </transition>

        <TableCard v-if="showChecklistTemplates" tablecard-class="roster-checklist-table-card mt-4 rounded-lg shadow">
            <template v-slot:table-filters>
                <ContainerFilter inTable>
                <template v-slot:title>
                    <span class="inline-block text-gray-600 text-base font-bold mr-2">Roster Checklist Templates</span>
                </template>
                </ContainerFilter>
            </template>
            
            <template v-slot:table-searchbox>
                <search-box inTable @search-data="searchData"/>
            </template>

            <!--Table-->
            <CustomTable
            inTable
            ref="table"
            v-loading="loading || !getSystemSettingOptions.length"
            :columns="cols"
            :data="filteredTableData"
            :totalRecords="totalRosterChecklists"
            emptyText="No Roster Checklists"
            footerTable="Total Roster Checklists"
            withTableMenu
            @click-menu="handleCommand($event, 'create-new-roster-checklist', {})"
            :optionsHeader="optionsContextHeader"
            :optionsRows="[]"
            :expandCollapseColumn="showExpandCollapseColumn"
            :paginationCurrentPage="1"
            :pagination="false"
            >
                <template #assignedToId="row">
                    {{ itemById(rosterChecklistAssignedToOptions, row.assignedToId, 'option') | text }}
                    <ol v-if="row.assignedToId">
                        <template v-if="showUserMenu(row)">
                            <li class="text-blue-700" v-for="(rclUsr, i) in (row.assignedUsers.items||[])" :key="row.id + rclUsr.id + i">
                                {{ itemById(userList, rclUsr.userId, (user) => user ? getFullName(user) : null) | text }}
                            </li>
                        </template>
                        <template v-else-if="row.checklistTasks">
                            <li class="text-blue-700" v-for="(rclChkbx, i) in checklistTaskAssociates(row)" :key="row.id + rclChkbx.id + i">
                                {{ itemById(rosterChecklistTasks, rclChkbx.taskId, 'option') | text }}
                            </li>
                        </template>
                    </ol>
                </template>
                <template #completionFrequencyId="row">
                    {{ itemById(rosterChecklistCompletionFrequencies, row.completionFrequencyId, 'option') | text }}
                </template>
                <template #items="row">
                    <ol>
                        <template v-if="row.rosterChecklistItems && row.rosterChecklistItems.items && row.rosterChecklistItems.items.length">
                            <li v-for="(item, i) in row.rosterChecklistItems.items" :key="row.id + item.id + i">
                                {{item.titleQuestion}} <span class="text-blue-700">({{ itemById(rosterChecklistItemTypes, item.typeId, 'option') | text }})</span>
                            </li>
                        </template>
                    </ol>
                </template>
                <template #status="row">
                    <div class="inline-flex">
                        <span
                        class="font-bold rounded-full bg-opacity-70 px-3 text-xs max-w-full flex-initial"
                        :class="{'bg-red-500 text-red-900': row.status !== 'ENABLED', 'bg-green-500 text-green-900': row.status === 'ENABLED'}"
                        >{{row.status === 'ENABLED'?'Enabled' : 'Disabled'}}</span>
                    </div>
                </template>
                <template #custom-dropdown="row">
                    <RosterChecklistAdvice>
                        <span class="context-menu mt-px mr-1" @click="(e)=>(!rowTransition.updatingTableRow && popoverHandler(e, 'click', row) )">
                            <i class="uil uil-ellipsis-h roster-checklist-popover-reference"></i>
                        </span>
                    </RosterChecklistAdvice>
                </template>
            </CustomTable>
        </TableCard>
        <el-popover
            v-for="count in 2" :key="count"
            ref="popoverElement"
            trigger="click"
            popper-class="checklist-popper"
            :open-delay="0"
            :close-delay="0"
            >
            <template v-if="popoverElement.item">
                <div class="checklist-menu w-full" v-loading="rowTransition.updatingTableRow === rowTransition.TRANSITION_ROW_STATUS_CHANGING">
                    <div class="checklist-menu-item leading-8" @click="handleCommand($event, 'start-edit-roster-checklist', popoverElement.item)">View this Roster Checklist Template</div>
                    <div class="checklist-menu-item leading-8" @click="handleCommand($event, 'copy-roster-checklist', popoverElement.item)">Duplicate this Roster Checklist Template</div>
                    <div class="el-dropdown-menu__item--divided"></div>
                    <div v-if="popoverElement.item.status !== 'ENABLED'" class="checklist-menu-item leading-8" data-cy="enable-roster-checklist-action" @click="handleCommand($event, 'enable-roster-checklist', popoverElement.item)">Enable this Roster Checklist Template</div>
                    <div v-else class="checklist-menu-item leading-8" data-cy="disable-roster-checklist-action" @click="handleCommand($event, 'disable-roster-checklist', popoverElement.item)">Disable this Roster Checklist Template</div>
                    <div class="el-dropdown-menu__item--divided"></div>
                    <div class="checklist-menu-item leading-8" @click="handleCommand($event, 'delete-roster-checklist', popoverElement.item)">Delete this Roster Checklist Template</div>
                </div>
            </template>
            <div v-else class="py-4" v-loading="true"/>

        </el-popover>
    </div>
</template>
<script>
import {API} from 'aws-amplify'
import TableCard from "@/components/TableCard/TableCard"
import ContainerFilter from "@/components/TableCard/ContainerFilter"
import SearchBox from "@/components/TableCard/SearchBox"
import CustomTable from "@/components/TableCard/CustomTable"
import rosterChecklistMixin from '../rosterChecklist'
import { mapState, mapGetters } from 'vuex'
import { updateTenantDetail } from '@/store/mutations'
import { getTenantFeaturePermissions } from '@/middlewares/permissions'
import RosterChecklistAdvice from '@/views/DailyRoster/components/RosterChecklistAdvice'
import { getFullName } from '@/utilities/associate'

export default{
    name: "RosterChecklistsTableCard",
    mixins: [ rosterChecklistMixin ],
    components: {
        TableCard,
        CustomTable,
        SearchBox,
        ContainerFilter,
        RosterChecklistAdvice
    },
    data(){
                
        const TRANSITION_ROW_UPDATE_FINISH = 0
        const TRANSITION_ROW_STATUS_CHANGING = 2
        return {
            loading: false,
            tableData: [],
            cols: [
                { name: "Roster Checklist Name", col: "name", fixed: false, width: "350", sortable: true, sortMethod: this.sortTableBy('name') },
                { name: "Items", col: "items", fixed: false, width: "475", sortable: true, sortMethod: this.sortTableBy('items') },
                { name: "Frequency", col: "completionFrequencyId", fixed: false, width: "175", sortable: true, sortMethod: this.sortTableBy('completionFrequency') },
                { name: "Assigned To", col: "assignedToId", fixed: false, width: "400", sortable: true, sortMethod: this.sortTableBy('assignedTo') },
                { name: "Status", col: "status", fixed: false, width: "100", sortable: true, sortMethod: this.sortTableBy('status')}
            ],
            search: null,
            showExpandCollapseColumn: true,
            currentPage: 1,
            popoverElement: {
                show: null,
                reference: null,
                item: null,
                previousStyle: null,
                disableUp: true,
                disableDown: true,
                popoverIndex: 0
            },
            rowTransition: {
                TRANSITION_ROW_UPDATE_FINISH,
                TRANSITION_ROW_STATUS_CHANGING,
                updatingTableRow: TRANSITION_ROW_UPDATE_FINISH
            },
            permissionRosterChecklist: {
                value: false,
                loading: false
            }
        }
    },
    computed:{
        ...mapState(['valuelists', 'userInfo']),
        ...mapGetters(['getSystemSettingOptions', 'hasLogguedSupportAdministrator', 'isSystemAdmin']),
        ...mapState('subscriptionStore', [
            'userList',
        ]),
        disabledPermissionRosterChecklist(){
            if(!this.featurePermissions.featureEnabledRosterChecklist){
                return true
            }
            if(this.userInfo.permissionFullAccess || this.isSystemAdmin){
                return false
            }
            return true
        },
        optionsContextHeader(){
            if(this.featurePermissions.permissionRosterChecklist){
                return [
                    { label: "Create New Roster Checklist Template", divided:  false }
                ]
            }
            return []
        },
        systemSettingOptionsLookupmap(){
            return this.getSystemSettingOptions?.reduce((map, item)=>(map[item.id] = item.option, map),{}) || {}
        },
        rosterChecklistCompletionFrequencies(){
            return this.getSystemSettingOptions.filter(o => o.systemSettingKey === "RCHKLST_CF")
        },
        rosterChecklistItemTypes(){
            return this.getSystemSettingOptions.filter(o => o.systemSettingKey === "RCHKLST_ITM_TYPE")
        },
        rosterChecklistAssignedToOptions(){
            return this.getSystemSettingOptions.filter(o => o.systemSettingKey === "RCHKLST_AS")
        },
        rosterChecklistTasks(){
            return this.getSystemSettingOptions.filter(o => ['RCHKLST_UT', 'RCHKLST_AT'].includes(o.systemSettingKey))
        },
        totalRosterChecklists(){
            return this.tableData?.length
        },
        filteredTableData(){
            return this.customFilterArray(this.tableData, this.search)
        },
        featurePermissions(){
            return getTenantFeaturePermissions()
        },
        showChecklistTemplates(){
            return this.featurePermissions.permissionRosterChecklist &&
                this.featurePermissions.featureEnabledRosterChecklist
        }
    },
    methods:{

        getFullName,

        async updatePermissionRosterChecklists(value){
            const input = {
                id: this.userInfo.tenant.id,
                permissionRosterChecklist: value
            }
            const response = await this.api(updateTenantDetail, {input: input})
            return response.data.updateTenant
        },

        async toggleRosterChecklistFeature(value){
            this.permissionRosterChecklist.loading = true
            try {
                const updated = await this.updatePermissionRosterChecklists(value)
                this.permissionRosterChecklist.value = updated.permissionRosterChecklist
            } catch (error) {
                this.displayUserError(error)
                this.permissionRosterChecklist.value = !value
            }
            this.permissionRosterChecklist.loading = false
        },

        checklistTaskAssociates(row){
            const checklistTasks = row.checklistTasks?.items || []
            return checklistTasks.filter(ct => ct.type === "ASSOCIATE_TASK")
        },
                
        customFilterArray(tableData, search){
            const found = this.filterArray(tableData, search)
            const itemsCollection = tableData.map(rcl => rcl.rosterChecklistItems?.items)
            let matchedByItems = []
            if(itemsCollection.length){
                const itemsFound = this.filterArray(itemsCollection, search)
                matchedByItems = tableData.filter(rcl=> itemsFound.some(items1=>items1 === (rcl.rosterChecklistItems?.items || [])))
            }
            const searchLower = (search || '').toLowerCase()
            const matchedByUser = searchLower ? tableData.filter(rcl => {
                if (!this.showUserMenu(rcl)) return false
                return (rcl.assignedUsers?.items || []).some(au => {
                    const user = this.userList?.[au.userId]
                    if (!user) return false
                    const fullName = getFullName(user) || ''
                    return fullName.toLowerCase().includes(searchLower)
                })
            }) : []
            const unique = new Set([...found, ...matchedByItems, ...matchedByUser])
            return tableData.filter(rcl => unique.has(rcl))
        },
        searchData(text){
            this.search = text
        },
        showUserMenu(row){
            const itemKey = this.itemById(this.rosterChecklistAssignedToOptions, row.assignedToId, 'optionKey')
            return itemKey === 'RCHKLST_AS_SPECIFIC_HERA_USERS'
        },
        async handleCommand(event, action, row){
            let notification
            switch(action){
                case 'create-new-roster-checklist':{
                    this.$router.push({
                        name: 'RosterChecklistEditor',
                        params: {
                            rosterChecklistId: 'create-new',
                            parentProps: {
                                tableData: this.tableData
                            }
                        }
                    })
                    break
                }
                case 'start-edit-roster-checklist':{
                    this.$router.push({
                        name: 'RosterChecklistEditor',
                        params: {
                            rosterChecklistId: row.id,
                            parentProps: {
                                formData: row,
                                tableData: this.tableData
                            }
                        }
                    })
                    break
                }
                case 'copy-roster-checklist':{
                    
                    this.rowTransition.updatingTableRow = this.rowTransition.TRANSITION_ROW_STATUS_CHANGING
                    
                    const rosterChecklistId = this.generateUUID()
                    const group = this.userInfo.tenant.group

                    const rosterChecklistInput = {
                        id: rosterChecklistId,
                        group,
                        name: this.copyRosterChecklistName(this.tableData, row.name, true),
                        completionFrequencyId: row.completionFrequencyId,
                        assignedToId: row.assignedToId,
                        sendTime: row.sendTime,
                        totalSimpleItems: row.totalSimpleItems,
                        totalVehicleItems: row.totalVehicleItems,
                        status: row.status
                    }

                    const usersInput = row.assignedUsers.items?.map(item => {
                        return {
                            input: {
                                userId: item.userId,
                                rosterChecklistId,
                                group
                            }
                        }
                    })

                    const tasksInput = row.checklistTasks.items?.map(item => {
                        return {
                            input: {
                                taskId: item.taskId,
                                type: item.type,
                                rosterChecklistId,
                                group
                            }
                        }
                    })
                    
                    const itemsInput = row.rosterChecklistItems.items?.map(item => {
                        return {
                            input: {
                                typeId: item.typeId,
                                titleQuestion: item.titleQuestion,
                                descriptionExplanation: item.descriptionExplanation,
                                order: item.order,
                                status: item.status,
                                photoSpecifications: item.photoSpecifications,
                                requiredPhotoSpecifications: item.requiredPhotoSpecifications,
                                options: item.options,
                                allowedFileTypes: item.allowedFileTypes,
                                maxFileSizeMB: item.maxFileSizeMB,
                                rosterChecklistId,
                                group
                            }
                        }
                    })

                    const vehicleTypesInput = row.vehicleTypes.items?.map(item => {
                        return {
                            input: {
                                vehicleTypeId: item.vehicleTypeId,
                                rosterChecklistId,
                                group
                            }
                        }
                    })

                    const multiQuery = {
                        RosterChecklist: {
                            create: {
                                input: rosterChecklistInput
                            },
                            fragmentName: "rosterChecklistFragment"
                        },
                        RosterChecklistUser:{
                            create: usersInput,
                            fragmentName: "rosterChecklistUserFragment"
                        },
                        RosterChecklistTask:{
                            create: tasksInput,
                            fragmentName: "rosterChecklistTaskFragment"
                        },
                        RosterChecklistItem: {
                            create: itemsInput,
                            fragmentName: "rosterChecklistItemFragment"
                        },
                        RosterChecklistVehicleType: {
                            create: vehicleTypesInput,
                            fragmentName: "rosterChecklistVehicleTypeFragment"
                        }
                    }
                    
                    let apiName = 'heraPublicApi';
                    let path = '/executeMutation';
                    let post = {
                        body: {
                            "type": 'rosterChecklist',
                            "mutationName": 'createRosterChecklist',
                            "multiQuery": multiQuery
                        }
                    }

                    const resCopy = await safeFunction(API.post)(apiName, path, post)

                    if(resCopy?.createRosterChecklist.length){
                        const newRosterChecklist = resCopy.createRosterChecklist[0]
                        const copiedRosterChecklist = {
                            ...newRosterChecklist,
                            "vehicleTypes": {
                                "items": resCopy.createRosterChecklistVehicleType || []
                            },
                            "assignedUsers": {
                                "items": resCopy.createRosterChecklistUser || []
                            },
                            "checklistTasks": {
                                "items": resCopy.createRosterChecklistTask || []
                            },
                            "rosterChecklistItems": {
                                "items": resCopy.createRosterChecklistItem || []
                            }
                        }
                        this.tableData.push(copiedRosterChecklist)

                        notification = {
                            type: 'checklist-create'
                        }
                    }

                    this.rowTransition.updatingTableRow = this.rowTransition.TRANSITION_ROW_UPDATE_FINISH
                    break
                }
                case 'enable-roster-checklist':{
                    if(row.status === 'ENABLED'){
                        break
                    }
                    this.rowTransition.updatingTableRow = this.rowTransition.TRANSITION_ROW_STATUS_CHANGING
                    const multiQuery = {
                        RosterChecklist: {
                            update: {
                                input: {
                                    id: row.id,
                                    status: 'ENABLED'
                                }
                            },
                            fragmentName: 'rosterChecklistFragment'
                        }
                    }

                    let apiName = 'heraPublicApi';
                    let path = '/executeMutation';
                    let post = {
                        body: {
                            "type": 'rosterChecklist',
                            "mutationName": 'updateRosterChecklist',
                            "multiQuery": multiQuery
                        }
                    }

                    const resUpdate = await safeFunction(API.post)(apiName, path, post)
                    
                    const newStatus = resUpdate.updateRosterChecklist[0].status

                    const rcl = this.tableData.find(rcl => rcl.id === row.id)

                    rcl.status = row.status = newStatus
                    
                    notification = {
                        type: 'checklist-status-change',
                        status: newStatus
                    }

                    this.rowTransition.updatingTableRow = this.rowTransition.TRANSITION_ROW_UPDATE_FINISH
                    
                    break
                }
                case 'disable-roster-checklist':{

                    this.popoverHandler(event, false, row)
                    
                    if(row.status === 'DISABLED'){
                        break
                    }

                    const performDisable = await this.$confirm(
                        'Any links that were sent for this checklist will be disabled. Scheduled checklist reminders that have not yet been sent will be canceled.\n\nAre you sure you want to continue?',
                        'Warning',
                    {
                        type: 'warning',
                        confirmButtonText: 'Disable',
                        cancelButtonText: 'Cancel',
                    }
                    ).catch(e => e);
                    if (performDisable === 'confirm'){

                        this.rowTransition.updatingTableRow = this.rowTransition.TRANSITION_ROW_STATUS_CHANGING
                        const multiQuery = {
                            RosterChecklist: {
                                update: {
                                    input: {
                                        id: row.id,
                                        status: 'DISABLED'
                                    }
                                },
                                fragmentName: 'rosterChecklistFragment'
                            }
                        }

                        let apiName = 'heraPublicApi';
                        let path = '/executeMutation';
                        let post = {
                            body: {
                                "type": 'rosterChecklist',
                                "mutationName": 'updateRosterChecklist',
                                "multiQuery": multiQuery
                            }
                        }

                        const resUpdate = await safeFunction(API.post)(apiName, path, post)

                        const newStatus = resUpdate.updateRosterChecklist[0].status

                        const rcl = this.tableData.find(rcl => rcl.id === row.id)

                        rcl.status = row.status = newStatus
                        
                        notification = {
                            type: 'checklist-status-change',
                            status: newStatus
                        }

                        this.rowTransition.updatingTableRow = this.rowTransition.TRANSITION_ROW_UPDATE_FINISH
                    }
                    break
                }
                case 'delete-roster-checklist':{

                    this.popoverHandler(event, false, row)

                    const performDelete = await this.$confirm('This will permanently delete this Roster Checklist Template. Continue?', 'Warning', {
                        type: 'warning',
                        confirmButtonText: 'Delete',
                        cancelButtonText: 'Cancel',
                    }).catch(e => e )

                    if(performDelete === 'confirm'){
                        this.loading = true
                        const usersInput = row.assignedUsers.items?.map(item => {
                            return {
                                input: {
                                    id: item.id
                                }
                            }
                        })

                        const tasksInput = row.checklistTasks.items?.map(item => {
                            return {
                                input: {
                                    id: item.id
                                }
                            }
                        })
                        
                        const itemsInput = row.rosterChecklistItems.items?.map(item => {
                            return {
                                input: {
                                    id: item.id
                                }
                            }
                        })

                        const vehicleTypesInput = row.vehicleTypes.items?.map(item => {
                            return {
                                input: {
                                    id: item.id
                                }
                            }
                        })

                        const multiQuery = {
                            RosterChecklist: {
                                delete: {
                                    input: {
                                        id: row.id
                                    }
                                },
                                fragmentName: "rosterChecklistFragment"
                            },
                            RosterChecklistUser:{
                                delete: usersInput,
                                fragmentName: "rosterChecklistUserFragment"
                            },
                            RosterChecklistTask:{
                                delete: tasksInput,
                                fragmentName: "rosterChecklistTaskFragment"
                            },
                            RosterChecklistItem: {
                                delete: itemsInput,
                                fragmentName: "rosterChecklistItemFragment"
                            },
                            RosterChecklistVehicleType: {
                                delete: vehicleTypesInput,
                                fragmentName: "rosterChecklistVehicleTypeFragment"
                            }
                        }
                        
                        let apiName = 'heraPublicApi';
                        let path = '/executeMutation';
                        let post = {
                            body: {
                                "type": 'rosterChecklist',
                                "mutationName": 'deleteRosterChecklist',
                                "multiQuery": multiQuery
                            }
                        }

                        const resDelete = await safeFunction(API.post)(apiName, path, post)
                        
                        if(resDelete?.deleteRosterChecklist.length){
                            const rowIndex = this.tableData.findIndex(rcl => rcl.id === row.id)
                            this.tableData.splice(rowIndex, 1)

                            notification = {
                                type: 'checklist-delete'
                            }
                        }

                        this.loading = false

                    }
                    
                    break
                }
            }
            if( !['start-edit-roster-checklist', 'delete-roster-checklist','disable-roster-checklist' ].some(a => a === action) ){
                this.popoverHandler(event, false, row)
            }
            if(notification){
                await this.sendCompanyNotification({
                    action,
                    rosterChecklistId: row.id,
                    ...notification
                })
            }
        },
        
        bodyClickHandler(event){
            event.preventDefault()
            if(!event.target.classList.contains('roster-checklist-popover-reference')){
                this.popoverHandler(event, false, this.popoverElement.item)
            }
        },

        popoverHandler(event, show, row){
            this.popoverElement.item = row
            const popoverEls = this.$refs['popoverElement']
            const oldPopper = popoverEls[this.popoverElement.popoverIndex]

            oldPopper.doClose()
            oldPopper.referenceElm = undefined
            oldPopper.updatePopper()

            this.popoverElement.popoverIndex = [1, 0][this.popoverElement.popoverIndex]

            if(show){
                const trEl = event.target.closest('tr')
                this.popoverElement.disableUp = !trEl.previousSibling || trEl.previousSibling.nodeName !== 'TR'
                this.popoverElement.disableDown = !trEl.nextSibling || trEl.nextSibling.nodeName !== 'TR'
                const newPopper = popoverEls[this.popoverElement.popoverIndex]
                const style = newPopper.$refs.popper.getAttribute('style')
                const displayNone = /display\s*:\s*none/gi.test(style)

                if(displayNone){
                    newPopper.referenceElm = event.target
                    newPopper.updatePopper()
                    newPopper.doShow()
                }
            }else{
                this.popoverElement.item = null
            }

        },

        sortTableBy(col){
            const sortMethods = {
                "name": (a, b) => a.name.localeCompare(b.name),
                "items": (a, b) => {
                    const itemA = a.rosterChecklistItems.items?.[0]
                    const itemB = b.rosterChecklistItems.items?.[0]
                    return itemA?.titleQuestion.localeCompare(itemB?.titleQuestion) || 1
                },
                "completionFrequency": (a, b) => {
                    const completionFrequencyA = this.systemSettingOptionsLookupmap[a.completionFrequencyId]
                    const completionFrequencyB = this.systemSettingOptionsLookupmap[b.completionFrequencyId]
                    return completionFrequencyA?.localeCompare(completionFrequencyB) || 1
                },
                "assignedTo": (a, b) => {
                    const assignedToA = this.systemSettingOptionsLookupmap[a.assignedToId]
                    const assignedToB = this.systemSettingOptionsLookupmap[b.assignedToId]
                    return assignedToA?.localeCompare(assignedToB) || 1
                },
                "status": (a, b) => a.status.localeCompare(b.status)
            }
            return sortMethods[col]
        },

        loadData: async function(){
            this.loading = true
            let tableData = await this.getRosterChecklists()
            tableData.forEach(rcl => {
                if(rcl.rosterChecklistItems?.items){
                    rcl.rosterChecklistItems.items = rcl.rosterChecklistItems.items.sort((a, b)=> a.order - b.order)
                }
            })
            this.tableData = tableData
            this.loading = false
        }
    },
    created(){
        this.$nextTick(()=>{
            document.body.addEventListener('click', this.bodyClickHandler)
        })
    },
    async mounted(){
        this.permissionRosterChecklist.value = this.userInfo.tenant.permissionRosterChecklist
        await this.loadData()
    },
    beforeDestroy(){
        document.body.removeEventListener('click', this.bodyClickHandler)
    }
}
</script>
<style>
    .checklist-popper{
        @apply px-0 py-2 w-auto !important;
    }
    .checklist-menu .checklist-menu-item{
        @apply text-gray-700 hover:text-blue-600 hover:bg-gray-200 cursor-pointer whitespace-nowrap;
    }
    .checklist-menu .el-dropdown-menu__item--divided::before{
        @apply bg-transparent !important;
    }
    .checklist-menu > div{
        @apply px-4 !important;
    }
</style>
<style lang="scss">
.checklist-template-index{
    .switch-loading{
        .el-switch__core::after {
            content: "";
            position: absolute;
            border: 2px solid theme('colors.blue.500');
            border-top-color: transparent;
            border-radius: 50%;
            animation: spin 1s linear infinite;
        }
    }
}
@keyframes spin {
    0% { transform: translate(-50%, -50%) rotate(0deg); }
    100% { transform: translate(-50%, -50%) rotate(360deg); }
}
.roster-checklist-table-card{
    .cell ol {
        > li::before {
            @apply text-blue-700 font-bold inline-block w-1 ml-0;
            content: "\2022";
        }
        > li{
            padding-left: 7px;
            text-indent: -7px;
        }
    }
    .title-container{
        text-align: unset !important;
        text-transform: none !important;
    }
}
</style>
